<template>
  <div
    :class="{ active: currentId === file.id }"
    class="item">
    <div class="container preview-content">
      <div class="preview-img">
        <!-- preload -->
        <div
          v-if="loading"
          class="preload-box">
          <div class="spinner">
            <div class="rect1" />
            <div class="rect2" />
            <div class="rect3" />
            <div class="rect4" />
            <div class="rect5" />
          </div>
        </div>

        <!-- image -->
        <img
          v-if="previewType === 'image' && !loading && !noPreview"
          :src="getSrc"
          :alt="file.name"
          @contextmenu="onContextMenu($event, { file })"
          @load="resizeImg"
          @error="errorImageLoadDoc">

        <!-- video -->
        <div
          v-if="previewType === 'video' && !loading"
          class="player">
          <video
            :id="`video-${file.id}`"
            :data-videoid="file.id"
            controls="true"
            @keydown.right.stop="moveToNext"
            @keydown.left.stop="moveToPrev">
            <source
              :src="getSrc"
              type="video/mp4">
          </video>
        </div>

        <!-- document -->
        <object
          v-if="previewType === 'document' && !loading"
          :data="getSrc"
          width="100%"
          height="100%">
          <iframe
            :src="getSrc"
            title="Document preview"
            width="100%"
            height="100%"
            style="border: none" />
        </object>

        <!-- document-zip -->
        <div
          v-if="previewType === 'document-zip' && !loading && !noPreview"
          class="scrolled-block">
          <img
            v-for="(slide, index) in file.content"
            :key="`slide-${index}`"
            :src="slide"
            alt="image"
            @contextmenu="onContextMenu($event, { force: true })"
            @load="resizeImg"
            @error="errorImageLoadDoc">
        </div>

        <!-- no preview -->
        <div
          v-if="(!previewType && !loading) || noPreview"
          class="no-preview">
          <p v-translate>
            sorry
          </p>
          <p>
            <em class="qx-icon icon-eye-close" />
          </p>
          <p v-html="$sanitize(noPreviewText)" />
          <qx-button
            v-if="isDownloadPermission"
            id="download"
            :title="downloadButtonLabel"
            :action="downloadFile"
            state="active" />
        </div>
      </div>
    </div>
    <div class="container preview-metadata">
      <p
        :title="file.name"
        class="preview-metadata-title">
        {{ file.name }}
      </p>
      <p class="preview-metadata-tools" />
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue';
import { mapGetters, mapActions, mapMutations } from 'vuex';

import qxButton from '@/elements/button.vue';
import EventBus from '@/elements/eventBus.js';

import { http } from '@/api/httpModule.js';
import { getFilePreview } from '@/api';
import JSZip from 'jszip';
import { logErrorWithSentry } from '@/elements/utils.js';
import { prepareURL } from '@/api/httpModule';

const IMG_MAX_WIDTH = 1024;

export default {
  name: 'PreviewSlide',
  components: {
    qxButton,
  },
  props: {
    file: {
      type: Object,
      required: true,
    },
    currentId: {
      type: String,
      required: true,
    },
    isSharePreview: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['next', 'prev', 'update:file'],
  data() {
    return {
      loading: true,
      previewType: null,
      noPreview: false,
    };
  },
  computed: {
    ...mapGetters({
      checkOperations: 'files/checkOperations',
    }),
    noPreviewText() {
      return this.$gettextInterpolate(this.$gettext('no preview%{ breakTag }available'), { breakTag: '<br>' }, true);
    },
    getSrc() {
      return prepareURL(this.file.source);
    },
    downloadButtonLabel() {
      return this.$gettext('Download');
    },
    isDownloadPermission() {
      return this.checkOperations(this.file, 'download');
    },
  },
  mounted() {
    this.renderElement();
  },
  methods: {
    ...mapMutations({
      updateFile: 'files/updateFile',
    }),
    ...mapActions({
      downloadFileAction: 'files/downloadFile',
    }),

    errorImageLoadDoc() {
      this.noPreview = true;
    },

    getZipSource() {
      http
        .get(this.file.source, { responseType: 'blob' })
        .then((response) => {
          if (response.status === 200 || response.status === 0) {
            // prepare array for images that is in zip
            this.$emit('update:file', { ...this.file, content: [] });
            try {
              return Promise.resolve(new File([response.data], 'zip'));
            } catch (e) {
              // for IE11
              let file = response.data;
              file.lastModifiedDate = new Date();
              file.name = 'zip';
              return Promise.resolve(file);
            }
          } else {
            return Promise.reject(new Error(response.statusText));
          }
        })
        .then(JSZip.loadAsync)
        .then((zip) => {
          zip.forEach((index, image) => {
            return zip
              .file(image.name)
              .async('uint8array')
              .then((content) => {
                const contentIndex = index.match(/\d+/)[0] - 1; // to start from 0
                // read each image and put it into content
                // for correct orger get index from image name
                const contentArr = [...this.file.content];
                contentArr[contentIndex] = window.URL.createObjectURL(
                  new Blob([content.buffer], { type: 'image/png' })
                );
                this.noPreview = false;
                this.$emit('update:file', { ...this.file, content: contentArr });
              });
          });
        })
        .catch((error) => {
          this.errorImageLoadDoc();
        });
    },
    onContextMenu(event, options = {}) {
      if (options.force || (options.file && !this.isDownloadPermission)) {
        event.preventDefault();
        return false;
      }
    },
    renderElement() {
      if (Boolean(this.file.source) && Boolean(this.file.previewType)) {
        this.loading = false;
      } else {
        this.getPreview();
      }
    },
    resizeImg: function () {
      const imgBlock = $(this.$el).find('.preview-img');
      if (parseInt(imgBlock.width(), 10) < IMG_MAX_WIDTH) {
        imgBlock.find('img').css('maxWidth', '100%');
      } else {
        imgBlock.find('img').css('maxWidth', IMG_MAX_WIDTH);
      }
    },
    async downloadFile() {
      try {
        await this.downloadFileAction({
          list: [this.file.id],
          name: this.file.name,
        });
      } catch (error) {
        const msg =
          error.response?.data?.code === 90
            ? this.$gettext('You cannot download this folder because it is empty')
            : this.$gettext('File not found. Refresh file list and try again.');
        EventBus.$emit('handle-error', { response: { data: { msg } } });
        logErrorWithSentry(error);
      }
    },
    async getPreview() {
      try {
        const { data } = await getFilePreview(this.file.id, this.isSharePreview);
        this.$emit('update:file', {
          ...this.file,
          source: data.uri,
          previewType: data.type,
        });
        this.updateFile(this.file);
        await nextTick();
        this.previewType = this.file.previewType;
        this.loading = false;
        if (data.type === 'video') {
          this.setVideoDimensions();
        }
        if (data.type === 'document-zip') {
          this.getZipSource();
        }
      } catch (error) {
        this.previewType = null;
        this.loading = false;
      }
    },
    setVideoDimensions() {
      const video = document.getElementById(`video-${this.file.id}`);
      if (video) {
        video.width = '640px';
        video.height = '368px';
      }
    },
    moveToNext() {
      this.$emit('next');
    },
    moveToPrev() {
      this.$emit('prev');
    },
  },
};
</script>

<style lang="scss" scoped>
.preview-content {
  width: 100%;
  height: 80%;
}
.preview-metadata {
  height: 20%;
  width: 100%;
}
.preview-metadata-title {
  text-align: center;
  font-size: 2em;
  color: $white;
  max-height: 2.8em;
  overflow: hidden;
  word-break: break-word;
}
.preview-metadata-tools {
  color: $white;
  text-align: center;
  font-size: 1.6em;
}
.preview-img {
  width: 100%;
  height: 100%;
  @extend %dspFlex;
  @include justify-content(center);
  @include align-items(center);
  overflow: hidden;
  .player {
    @extend %dspFlex;
    height: 100%;
    @include align-items(center);
    @include justify-content(center);
    video {
      max-width: 100%;
    }
  }
  & > img {
    height: auto;
    max-height: 100%;
  }
  .scrolled-block {
    overflow-y: auto;
    height: 100%;
  }
}
.preload-box {
  margin: 40% auto;
  font-size: 92px;
  color: $white;
}
.no-preview {
  text-align: center;
  color: $white;
  font-size: 2em;
  .qx-icon {
    font-size: 30px;
  }
}
</style>
